// Session SC2B: seeding the Call-Out Attendance Report form from a call-out's own record.
//
// The Call-out Hub page and the Call-outs list each built this seed from LOADED registers of lifts and sites (they fetched every customer,
// site and lift when they opened, 2,400 to 5,900 rows at 1,000 lifts, only to read one lift's type, make and serial and one site's address).
// The call-out detail now carries those few facts itself (CalloutDetail.reportSeed), so the seed is built from the detail alone. One home, so
// the two pages cannot drift: it is the field-for-field mapping each used to carry.
import { apiRequest } from "../../lib/api";
import type { CalloutDetail, CalloutReportInput } from "../../lib/api";
import type { CalloutReportInitial } from "./CalloutReportModal";

/**
 * The site a STORED report form's lift belongs to, so the site picker opens on it. The form does not store its site. When the stored lift is the
 * call-out's own, the detail already says (siteId); only a form regenerated for a DIFFERENT lift needs one read of that lift. Null when unknown.
 */
export async function storedFormSiteId(d: CalloutDetail): Promise<string | null> {
  const stored = d.reportForm as (CalloutReportInput & { assetId?: string | null }) | null;
  if (!stored?.assetId) return null;
  if (stored.assetId === d.assetId) return d.siteId;
  try {
    const row = await apiRequest<{ siteId?: string | null; site?: { id?: string } | null }>(`/assets/${encodeURIComponent(stored.assetId)}`);
    return row.siteId ?? row.site?.id ?? null;
  } catch {
    return null;
  }
}

/** The report modal's seed, from the call-out's detail. `storedSiteId` is storedFormSiteId's answer (the site of a stored form's lift). */
export function buildReportInitial(d: CalloutDetail, storedSiteId: string | null): CalloutReportInitial {
  const seed = d.reportSeed ?? null;
  const isOneOff = !d.customerId;
  // Session ASQ (Stream R, R6): the price already agreed on the call-out (at intake, or on an earlier report) is billed
  // by its report unless someone clears it, so the form is seeded with it and the office SEES it. The record is the
  // authority, over a stored form written before the price was set.
  const agreedSeed = { agreedPricePence: d.agreedPricePence ?? null, agreedPriceReason: d.agreedPriceReason ?? null };
  const basicForm: CalloutReportInitial["form"] = {
    ...agreedSeed,
    calloutId: d.id,
    customerMode: isOneOff ? "oneoff" : "existing",
    customerId: d.customerId,
    siteId: d.siteId,
    assetId: d.assetId,
    siteName: isOneOff ? "" : d.siteName === "No site linked" ? "" : d.siteName,
    siteAddress: seed?.siteAddress ?? "",
    liftDesignation: d.assetRef ?? "",
    liftType: seed?.liftType ?? "",
    manufacturer: seed?.manufacturer ?? "",
    serialNumber: seed?.serialNumber ?? "",
    controllerDrive: seed?.controllerDrive ?? "",
    faultReported: d.faultDescription ?? "",
    dateReported: d.reportedAt ? d.reportedAt.slice(0, 10) : undefined,
    dateAttended: d.attendedAt ? d.attendedAt.slice(0, 10) : undefined,
  };
  const matchedContractRateName = d.contractRateApplied?.customerName ?? (d.contractRateApplied ? "the matched customer" : null);
  const common = {
    calloutId: d.id,
    reportUrl: d.reportUrl,
    hasReportPdf: d.hasReportPdf,
    currentReportRef: d.reportRef,
    reportSentAt: d.reportSentAt,
    reportSentTo: d.reportSentTo,
    matchedContractRateName,
  };
  const storedForm = d.reportForm as (CalloutReportInput & { assetId?: string | null }) | null;
  if (storedForm) {
    // Regenerate: re-fill from the stored form, with the site derived from its lift so the pickers pre-select.
    return { ...common, form: { ...storedForm, calloutId: d.id, siteId: storedSiteId, ...agreedSeed } };
  }
  // Enrich a one-off call-out (no linked Customer) with its stored ad-hoc caller identity + address, so the office does not have to
  // re-type known details: the ad-hoc address goes in Site address (not Site name), and the caller name/email/phone into the one-off fields.
  const form: CalloutReportInitial["form"] = isOneOff
    ? {
        ...basicForm,
        siteAddress: d.adhocSiteAddress ?? basicForm.siteAddress ?? "",
        oneOff: { companyName: d.adhocCustomerName ?? "", contactName: "", email: d.adhocEmail ?? "", phone: d.adhocPhone ?? "", address: d.adhocSiteAddress ?? "" },
      }
    : basicForm;
  return { ...common, form };
}
